import React, { useState } from 'react';
import classNames from 'classnames';
import { connect } from 'react-redux';

// Actions
import { ADD_FUNDS_TO_PORTFOLIO } from '@/redux/modules/edit-portfolio';
import { selectIsLoadingEvery } from '@/redux/modules/ui/loaders';

// Components
import EditPortfolioScreener from '@/views/components/edit-portfolio/EditPortfolioScreener';
import ScreenerContent from '@/views/containers/search/pages/ScreenerContent';
import ScreenerInstrumentTabs from '@/views/containers/search/partials/ScreenerInstrumentTabs';
import { withErrorBoundary } from '@/views/components/errors';
import Modal from '@/views/components/modals/AccessibleModal';

// Hooks
import useEnhancedScreener from '@/hooks/useEnhancedScreener';

// Types
import AdvancedSearchItem from '@/types/advanced-search-item';
import PortfolioHolding from '@/types/portfolio-holding';
import { MultiselectAction } from '@/types/data-table';
import { ReduxState } from '@/redux/modules/types';

// Utils
import { ADVANCED_SEARCH_OPTIONS, MODAL_SIZES, SCREENER_TAB_MODES } from '@/utils/constants';
import { formatMessage, FormattedMessage } from '@/utils/i18n';

type StateProps = {
  addingItems: boolean;
  holdingToReplace?: PortfolioHolding;
};

type Props = StateProps & {
  onAddItem: () => void;
  onAddItems: () => void;
  onClose: () => void;
  show: boolean;
};

const PortfolioScreenerModal = ({ addingItems, holdingToReplace, onAddItem, onAddItems, onClose, show }: Props) => {
  const { user, shouldUseEnhancedScreener } = useEnhancedScreener();
  const [selectedInstrument, setSelectedInstrument] = useState<ADVANCED_SEARCH_OPTIONS>(ADVANCED_SEARCH_OPTIONS.FUNDS);

  const getFundScreenerActions = (): MultiselectAction<AdvancedSearchItem>[] => [
    {
      loading: addingItems,
      icon: 'plus',
      label: <FormattedMessage id={`Search.Funds.${addingItems ? 'Adding' : 'Add'}ToPortfolio`} />,
      onClick: onAddItems,
    },
  ];

  return (
    <Modal backdropClassName="modal-v2-backdrop" className={classNames('screener-modal')} onHide={onClose} show={show} size={MODAL_SIZES.XXL}>
      {shouldUseEnhancedScreener && (
        <Modal.Header closeButton>
          <Modal.Title>{formatMessage('Screener.Title')}</Modal.Title>
          <div className="screener-tabs">
            <ScreenerInstrumentTabs user={user} mode={SCREENER_TAB_MODES.CONTROLLED} selectedId={selectedInstrument} onSelect={setSelectedInstrument} />
          </div>
        </Modal.Header>
      )}
      <Modal.Body className="d-flex flex-column">
        {shouldUseEnhancedScreener ? (
          <div className="screener screener-modal-container">
            <ScreenerContent instrumentType={selectedInstrument} insideModal />
          </div>
        ) : (
          <EditPortfolioScreener
            customTableActions={getFundScreenerActions()}
            hideExpandButton
            isExpanded
            showInlineAdd={!!holdingToReplace}
            onAddItem={onAddItem}
            onClose={onClose}
            selectedOption={ADVANCED_SEARCH_OPTIONS.FUNDS}
            title={formatMessage('EditPortfolioModal.FundScreener')}
          />
        )}
      </Modal.Body>
      {!shouldUseEnhancedScreener && <Modal.Footer />}
    </Modal>
  );
};

const mapStateToProps = (state: ReduxState) => ({
  addingItems: selectIsLoadingEvery(state, [ADD_FUNDS_TO_PORTFOLIO.type]),
  holdingToReplace: state.editPortfolio.holdingToReplace,
});

export default connect(mapStateToProps, {})(withErrorBoundary(PortfolioScreenerModal));
